import { Edit, useForm } from "@refinedev/antd";
import {
  Form,
  Input,
  Switch,
  Slider,
  Card,
  Typography,
  Space,
  Tag,
  Alert,
} from "antd";
import type { FeatureFlag } from "@/lib/types";

const { Text } = Typography;

const MARKS = {
  0: "0%",
  25: "25%",
  50: "50%",
  75: "75%",
  100: "100%",
};

/**
 * /admin/feature-flags/:id/edit — edição completa de uma flag.
 *
 * Como obtém os dados (MOCK MODE): `useForm` faz getOne (FIXTURES) para popular o
 * formulário e, ao salvar, PATCH /admin/feature-flags/:id (update no mock faz
 * merge e bumpa updated_at). A `key` é imutável; editam-se descrição, estado e
 * rollout (%). Com a API real (VITE_MOCK=false) o fluxo é idêntico.
 */
export const FeatureFlagEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<FeatureFlag>();
  const flag = queryResult?.data?.data;

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      title={
        <Space>
          <span>Editar feature flag</span>
          {flag?.key && <Tag color="cyan">{flag.key}</Tag>}
        </Space>
      }
    >
      <Card className="mel-glass" bordered={false}>
        <Form {...formProps} layout="vertical">
          <Form.Item
            label="Key"
            name="key"
            extra="Identificador imutável usado no código (não pode ser alterado)."
          >
            <Input
              disabled
              addonBefore={<code style={{ fontSize: 12 }}>flag</code>}
            />
          </Form.Item>

          <Form.Item
            label="Descrição"
            name="description"
            rules={[{ max: 200, message: "Máximo de 200 caracteres." }]}
          >
            <Input.TextArea
              rows={3}
              showCount
              maxLength={200}
              placeholder="O que esta flag controla e por quê."
            />
          </Form.Item>

          <Form.Item
            label="Ativa"
            name="enabled"
            valuePropName="checked"
            extra="Liga ou desliga a funcionalidade por completo."
          >
            <Switch checkedChildren="on" unCheckedChildren="off" />
          </Form.Item>

          <Form.Item
            label="Rollout (%)"
            name="rollout_percent"
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Percentual de usuários elegíveis que recebem a funcionalidade
                quando a flag está ativa. Use 0% para preparar sem expor e 100%
                para liberar a todos.
              </Text>
            }
          >
            <Slider
              min={0}
              max={100}
              step={5}
              marks={MARKS}
              tooltip={{ formatter: (v) => `${v}%` }}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            message="Mudanças entram em vigor imediatamente"
            description="Ao salvar, a flag é atualizada via PATCH /admin/feature-flags/:id e o horário de atualização é registrado."
          />
        </Form>
      </Card>
    </Edit>
  );
};
